
// Action: file_editor create /app/frontend/src/components/Portfolio.js --file-text 
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { portfolioData } from '../data/portfolio';

const Portfolio = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  return (
    <section data-testid="portfolio-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="font-manrope text-sm tracking-widest uppercase text-[#D4AF37] mb-4">
            Our Portfolio
          </p>
          <h2 className="font-playfair text-4xl md:text-5xl font-bold text-[#1A1A1A] mb-6 tracking-tight">
            Explore Our <span className="italic">Masterpieces</span>
          </h2>
          <p className="font-manrope text-base md:text-lg text-[#666666] max-w-2xl mx-auto">
            Discover our collection of beautifully designed spaces that showcase 
            our commitment to excellence and attention to detail.
          </p>
        </motion.div>

        {/* Masonry Gallery */}
        <div className="columns-1 md:columns-2 lg:columns-3 gap-8">
          {portfolioData.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="mb-8 break-inside-avoid group cursor-pointer"
              onClick={() => setSelectedImage(item)}
              data-testid={`portfolio-item-${item.id}`}
            >
              <div className="relative overflow-hidden rounded-sm">
                <motion.img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                  <p className="font-manrope text-xs tracking-widest uppercase text-[#D4AF37] mb-2">
                    {item.category}
                  </p>
                  <h3 className="font-playfair text-2xl font-semibold text-white">
                    {item.title}
                  </h3>
                  <p className="font-manrope text-sm text-gray-300 mt-2">
                    {item.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
            data-testid="lightbox-modal"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", damping: 25 }}
              className="relative max-w-5xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={() => setSelectedImage(null)}
                data-testid="lightbox-close-button"
                className="absolute -top-12 right-0 text-white hover:text-[#D4AF37] transition-colors duration-300"
              >
                <X size={32} />
              </button>

              {/* Image */}
              <img
                src={selectedImage.image}
                alt={selectedImage.title}
                className="w-full h-auto max-h-[80vh] object-contain rounded-sm"
              />

              {/* Image Info */}
              <div className="mt-6 text-center">
                <p className="font-manrope text-xs tracking-widest uppercase text-[#D4AF37] mb-2">
                  {selectedImage.category}
                </p>
                <h3 className="font-playfair text-3xl font-semibold text-white mb-2">
                  {selectedImage.title}
                </h3>
                <p className="font-manrope text-gray-300">
                  {selectedImage.description}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Portfolio;

// Observation: Create successful: /app/frontend/src/components/Portfolio.js